<div class="custom-views-container">
  <div class="header-section">
    <h1>Custom Views</h1>
    <p class="description">Select and view your custom data views</p>
  </div>

  <!-- Loading State -->
  <div *ngIf="loadingViews" class="loading-container">
    <mat-spinner diameter="30"></mat-spinner>
    <p>Loading available views...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loadingViews" class="error-container">
    <mat-icon color="warn">error</mat-icon>
    <p>{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadViews()">Retry</button>
  </div>

  <!-- Views Selector -->
  <div *ngIf="!loadingViews && !error" class="views-selector">
    <mat-form-field appearance="outline">
      <mat-label>Select a View</mat-label>
      <mat-select [(value)]="selectedView" (selectionChange)="onViewChange($event)">
        <mat-option [value]="null">None</mat-option>
        <mat-optgroup label="My Views" *ngIf="myViews.length > 0">
          <mat-option *ngFor="let view of myViews" [value]="view">
            <mat-icon class="view-icon">visibility</mat-icon>
            {{ view.name }}
            <span class="view-description" *ngIf="view.description"> - {{ view.description }}</span>
          </mat-option>
        </mat-optgroup>
        <mat-optgroup label="Shared Views" *ngIf="sharedViews.length > 0">
          <mat-option *ngFor="let view of sharedViews" [value]="view">
            <mat-icon class="view-icon">group</mat-icon>
            {{ view.name }}
            <span class="view-description" *ngIf="view.description"> - {{ view.description }}</span>
          </mat-option>
        </mat-optgroup>
        <mat-option *ngIf="myViews.length === 0 && sharedViews.length === 0" [value]="null" disabled>
          No views available
        </mat-option>
      </mat-select>
    </mat-form-field>

    <button mat-button color="accent" *ngIf="selectedView" (click)="refreshView()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>

    <button
      mat-raised-button
      [color]="pdfEditMode ? 'accent' : 'warn'"
      *ngIf="selectedView && isPdfTab()"
      (click)="togglePdfEditMode()">
      <mat-icon>{{ pdfEditMode ? 'edit_off' : 'edit' }}</mat-icon>
      {{ pdfEditMode ? 'Exit PDF Edit' : 'Edit PDF' }}
    </button>

    <button
      mat-raised-button
      color="warn"
      *ngIf="selectedView && isPdfTab() && pdfEditMode"
      [matMenuTriggerFor]="addCellMenu">
      <mat-icon>add</mat-icon>
      Add Cell
    </button>
    <mat-menu #addCellMenu="matMenu">
      <button mat-menu-item (click)="addPdfCell('text')">
        <mat-icon>text_fields</mat-icon>
        <span>Text</span>
      </button>
      <button mat-menu-item (click)="addPdfCell('image')">
        <mat-icon>image</mat-icon>
        <span>Image</span>
      </button>
    </mat-menu>

    <button
      mat-raised-button
      color="primary"
      *ngIf="selectedView && isPdfTab()"
      (click)="generatePdf()"
      [disabled]="generatingPdf">
      <mat-icon *ngIf="!generatingPdf">picture_as_pdf</mat-icon>
      <mat-spinner *ngIf="generatingPdf" diameter="20"></mat-spinner>
      {{ generatingPdf ? 'Generating...' : 'Generate PDF' }}
    </button>
  </div>

  <mat-divider></mat-divider>

  <div class="view-content">
    <!-- Display selected view content -->
    <div *ngIf="!selectedView && !loadingViews && !error" class="empty-state">
      <mat-icon>dashboard_customize</mat-icon>
      <h3>No view selected</h3>
      <p *ngIf="myViews.length > 0 || sharedViews.length > 0">Select a view from the dropdown above to display data</p>
      <p *ngIf="myViews.length === 0 && sharedViews.length === 0">No custom views are available yet.</p>
      <p class="admin-hint">
        <mat-icon>info</mat-icon>
        Views can be created and managed in the Admin → Custom Views section
      </p>
    </div>

    <!-- View display area when a view is selected -->
    <div *ngIf="selectedView && currentViewDefinition" class="view-display">
      <div class="view-header">
        <h2>{{ selectedView.name }}</h2>
        <p class="view-description">{{ selectedView.description }}</p>
        <div class="view-metadata">
          <span><mat-icon>person</mat-icon> Created by: {{ selectedView.createdBy }}</span>
          <span><mat-icon>calendar_today</mat-icon> Last modified: {{ selectedView.lastModified | date }}</span>
        </div>
      </div>

      <!-- Tab Navigation (if multiple tabs) -->
      <div *ngIf="currentViewDefinition.tabs.length > 1" class="view-tabs">
        <button
          *ngFor="let tab of getSortedTabs()"
          mat-button
          [class.active]="currentTab?.id === tab.id"
          [class.disabled]="!isTabEnabled(tab)"
          [disabled]="!isTabEnabled(tab)"
          [matTooltip]="getTabDisabledReason(tab)"
          [matTooltipDisabled]="isTabEnabled(tab)"
          (click)="onTabSelected(tab)">
          <mat-icon *ngIf="tab.tabType === 'pdf'" class="tab-type-icon">picture_as_pdf</mat-icon>
          <mat-icon *ngIf="!isTabEnabled(tab)" class="tab-lock-icon">lock</mat-icon>
          {{ tab.name }}
        </button>
      </div>

      <!-- PDF Tab Indicator -->
      <div *ngIf="isPdfTab() && !pdfEditMode" class="pdf-tab-indicator">
        <mat-icon>picture_as_pdf</mat-icon>
        <span>PDF Tab - Click "Generate PDF" to create a downloadable PDF document</span>
      </div>
      <div *ngIf="isPdfTab() && pdfEditMode" class="pdf-tab-indicator pdf-edit-indicator">
        <mat-icon>edit</mat-icon>
        <span>PDF Edit Mode - Click any cell to edit its text. Changes are for this PDF only and will not be saved.</span>
      </div>

      <!-- View Canvas -->
      <div *ngIf="currentTab && !loading" class="view-canvas-container">
        <div
          class="view-canvas"
          [style.width.px]="currentTab.canvas.width"
          [style.height.px]="getAdjustedCanvasHeight()">

          <!-- Repeating Sections -->
          <div *ngFor="let si of sectionInstances; trackBy: trackBySectionId"
               class="section-container"
               [style.top.px]="getAdjustedCellY(si.section.y)"
               [style.width.px]="currentTab.canvas.width"
               [style.height.px]="si.totalHeight">

            <!-- One row band per data item -->
            <div *ngFor="let row of si.rows"
                 class="section-row"
                 [style.top.px]="row.rowIndex * si.rowHeight"
                 [style.height.px]="si.rowHeight"
                 [class.alternate-row]="row.rowIndex % 2 === 1 && si.section.alternateRowColor"
                 [style.backgroundColor]="row.rowIndex % 2 === 1 && si.section.alternateRowColor ? si.section.alternateRowColor : null"
                 [class.row-border]="si.section.rowBorder !== false">
              <div *ngFor="let cellInstance of row.cellInstances; trackBy: trackByCellId"
                   class="cell-container"
                   [class.pdf-edit-active]="pdfEditMode"
                   [class.pdf-dragging]="pdfEditMode && pdfDraggingCellId === cellInstance.id"
                   [style.left.px]="cellInstance.viewCell.position.x"
                   [style.top.px]="cellInstance.viewCell.position.y"
                   [style.width.px]="cellInstance.viewCell.position.width"
                   [style.height.px]="cellInstance.viewCell.position.height"
                   [style.z-index]="cellInstance.viewCell.position.zIndex != null ? cellInstance.viewCell.position.zIndex : 1">

                <!-- Inline editing overlay -->
                <div class="pdf-edit-overlay"
                     *ngIf="pdfEditMode && pdfEditingCellId === cellInstance.id"
                     contenteditable="true"
                     [attr.data-cell-id]="cellInstance.id"
                     (blur)="onPdfCellEditBlur(cellInstance, $event)"
                     (keydown.escape)="onPdfCellEditCancel()">
                </div>

                <!-- Override display (visible even outside edit mode so edits persist) -->
                <div class="pdf-override-display"
                     *ngIf="hasPdfOverride(cellInstance) && pdfEditingCellId !== cellInstance.id"
                     (click)="pdfEditMode ? onPdfCellEditStart(cellInstance) : null"
                     (mousedown)="pdfEditMode ? onPdfCellDragStart($event, cellInstance) : null"
                     [class.editable]="pdfEditMode">
                  {{ getPdfOverride(cellInstance) }}
                </div>

                <!-- Click target to start editing (transparent overlay) -->
                <div class="pdf-edit-click-target"
                     *ngIf="pdfEditMode && !hasPdfOverride(cellInstance) && pdfEditingCellId !== cellInstance.id"
                     (click)="onPdfCellEditStart(cellInstance)"
                     (mousedown)="onPdfCellDragStart($event, cellInstance)">
                  <mat-icon class="edit-hint-icon">edit</mat-icon>
                </div>

                <!-- Resize handle -->
                <div class="pdf-resize-handle"
                     *ngIf="pdfEditMode && pdfEditingCellId !== cellInstance.id"
                     (mousedown)="onPdfCellResizeStart($event, cellInstance)">
                </div>

                <!-- Delete handle -->
                <button class="pdf-delete-handle"
                        *ngIf="pdfEditMode && pdfEditingCellId !== cellInstance.id"
                        type="button"
                        title="Delete cell"
                        (mousedown)="$event.stopPropagation()"
                        (click)="deletePdfCell($event, cellInstance)">
                  <mat-icon>close</mat-icon>
                </button>

                <div class="cell-content full-content"
                     [class.hidden-by-override]="hasPdfOverride(cellInstance)">
                  <div class="cell-component-container" #sectionCellContainer
                       [attr.data-cell-id]="cellInstance.id">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Render each cell -->
          <div
            *ngFor="let cellInstance of cellInstances; trackBy: trackByCellId"
            class="cell-container"
            [class.pdf-edit-active]="pdfEditMode"
            [class.pdf-dragging]="pdfEditMode && pdfDraggingCellId === cellInstance.id"
            [style.left.px]="cellInstance.viewCell.position.x"
            [style.top.px]="getCellTop(cellInstance)"
            [style.width.px]="cellInstance.viewCell.position.width"
            [style.height.px]="cellInstance.viewCell.position.height"
            [style.z-index]="cellInstance.viewCell.position.zIndex != null ? cellInstance.viewCell.position.zIndex : 1">

            <!-- Inline editing overlay -->
            <div class="pdf-edit-overlay"
                 *ngIf="pdfEditMode && pdfEditingCellId === cellInstance.id"
                 contenteditable="true"
                 [attr.data-cell-id]="cellInstance.id"
                 (blur)="onPdfCellEditBlur(cellInstance, $event)"
                 (keydown.escape)="onPdfCellEditCancel()">
            </div>

            <!-- Override display (visible even outside edit mode so edits persist) -->
            <div class="pdf-override-display"
                 *ngIf="hasPdfOverride(cellInstance) && pdfEditingCellId !== cellInstance.id"
                 (click)="pdfEditMode ? onPdfCellEditStart(cellInstance) : null"
                 (mousedown)="pdfEditMode ? onPdfCellDragStart($event, cellInstance) : null"
                 [class.editable]="pdfEditMode">
              {{ getPdfOverride(cellInstance) }}
            </div>

            <!-- Click target to start editing (transparent overlay) -->
            <div class="pdf-edit-click-target"
                 *ngIf="pdfEditMode && !hasPdfOverride(cellInstance) && pdfEditingCellId !== cellInstance.id"
                 (click)="onPdfCellEditStart(cellInstance)"
                 (mousedown)="onPdfCellDragStart($event, cellInstance)">
              <mat-icon class="edit-hint-icon">edit</mat-icon>
            </div>

            <!-- Resize handle -->
            <div class="pdf-resize-handle"
                 *ngIf="pdfEditMode && pdfEditingCellId !== cellInstance.id"
                 (mousedown)="onPdfCellResizeStart($event, cellInstance)">
            </div>

            <!-- Delete handle -->
            <button class="pdf-delete-handle"
                    *ngIf="pdfEditMode && pdfEditingCellId !== cellInstance.id"
                    type="button"
                    title="Delete cell"
                    (mousedown)="$event.stopPropagation()"
                    (click)="deletePdfCell($event, cellInstance)">
              <mat-icon>close</mat-icon>
            </button>

            <!-- Cell Content (hidden when override display is showing) -->
            <div class="cell-content full-content"
                 [class.hidden-by-override]="hasPdfOverride(cellInstance)">
              <div class="cell-component-container" #cellContainer>
                <!-- Dynamic cell component will be inserted here -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading state for view data -->
      <div *ngIf="loading" class="view-loading">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading view data...</p>
      </div>

      <!-- Empty state when no cells -->
      <div *ngIf="currentTab && currentTab.cells.length === 0 && (!currentTab.repeatingSections || currentTab.repeatingSections.length === 0) && !loading" class="empty-canvas">
        <mat-icon>dashboard_customize</mat-icon>
        <p>This view doesn't have any content yet</p>
      </div>
    </div>
  </div>
</div>